// src/services/openai.ts

import { portfolioKnowledge } from '../utils/portfolioKnowledge';

export interface HistoryMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface StreamOptions {
  onChunk: (chunk: string) => void;
  onDone: () => void;
  onError: (error: Error) => void;
}

// ============================================
// API Key Functions
// ============================================

// The chat assistant talks to the Groq API, which uses the Groq key.
export function getGroqApiKey(): string {
  return (
    process.env.REACT_APP_GROQ_API_KEY ||
    process.env.VITE_GROQ_API_KEY ||
    ''
  ).trim();
}

// A real OpenAI key is only used for the optional OpenAI TTS/Whisper features.
export function getOpenAIApiKey(): string {
  return (
    process.env.REACT_APP_OPENAI_API_KEY ||
    process.env.VITE_OPENAI_API_KEY ||
    ''
  ).trim();
}

export function hasOpenAIApiKey(): boolean {
  const apiKey = getOpenAIApiKey();
  return !!apiKey && apiKey.length > 0 && apiKey !== 'your_openai_api_key_here';
}

export function hasChatApiKey(): boolean {
  const apiKey = getGroqApiKey();
  return !!apiKey && apiKey.length > 0 && apiKey !== 'your_groq_api_key_here';
}

// ============================================
// SYSTEM PROMPT - Forces AI to respond in PLAIN ENGLISH only
// ============================================
const SYSTEM_PROMPT = `You are GeniusMate, the AI personal assistant of Sarfraz Ahmed Sheru Bhai. Your ONLY job is to manage his portfolio website and answer questions about it.

CRITICAL RULES:
1. ONLY talk about Sarfraz Ahmed Sheru Bhai's portfolio and his portfolio website (pages, services, projects, skills, experience, education, certificates, recommendations, contact, social links, resume, theme, navigation).
2. NEVER answer about the Pakistani cricketer "Sarfraz Ahmed" or any other unrelated person/company with a similar name. This portfolio belongs to a web developer & designer. If asked about the cricketer, politely say you only know about Sarfraz's portfolio.
3. NEVER invent, guess, or add facts about the portfolio. Answer ONLY from the KNOWLEDGE section that is provided to you. If the information is not there, say you are not sure or ask the visitor.
4. NEVER answer general knowledge, news, current events, or personal questions. Politely decline and list what you CAN do (navigate pages, explore services/projects/skills, contact Sarfraz, open social links, change theme, download resume).
5. ALWAYS respond in PLAIN ENGLISH only - NEVER use Urdu, Hindi, or any other script.
6. Do NOT use Roman Urdu (like "Aap kaise hain?") - use proper English ("How are you?").
7. Use simple, clear English. Keep responses friendly, professional, and helpful.
8. Respond in 1-2 short sentences for actions, longer for detailed questions.
9. Your on-screen replies are always shown in English and read aloud in English.

Example responses:
- Correct: "You are on the home page."
- Correct: "Dark mode has been activated."
- Correct: "I am opening your GitHub profile."
- Correct: "Sarfraz Ahmed Sheru Bhai is a Full Stack Developer based in Pakistan. He has completed 25+ projects and 15+ happy clients."
- Correct: "I'm sorry, I can only help with Sarfraz's portfolio. I can navigate his pages, share his services, projects, or contact details."
- Wrong: "آپ ہوم پیج پر ہیں۔" (Urdu script)
- Wrong: "Aap home page par hain." (Roman Urdu)
- Wrong: Anything about the cricketer Sarfraz Ahmed, general news, or facts not present in the KNOWLEDGE section.

Always respond in plain English only and stay within the portfolio scope!`;

// ============================================
// Build Assistant Messages
// ============================================
export function buildAssistantMessages(
  context: { pathname: string; theme: string; language: 'ur' | 'en' },
  history: HistoryMessage[],
): ChatMessage[] {
  const contextMessage = `Current page: ${context.pathname}, Theme: ${context.theme}, User language preference: ${context.language}.

KNOWLEDGE (the ONLY source of truth about the portfolio - answer strictly from this data):
${JSON.stringify(portfolioKnowledge)}`;
  
  return [
    { role: 'system', content: SYSTEM_PROMPT },
    { role: 'system', content: contextMessage },
    ...history,
  ];
}

// ============================================
// Check if API Key is configured (alias)
// ============================================
export function hasApiKey(): boolean {
  return hasChatApiKey();
}

// Current, widely supported Groq model.
// llama-3.1-8b-instant: faster, cheaper, and has a much larger free-tier daily
// allowance than the 70B models (which hit the 100K tokens/day cap quickly).
const GROQ_MODEL = 'llama-3.1-8b-instant';

// ============================================
// Stream Chat Completion via Groq API
// ============================================
export async function streamChatCompletion(
  messages: ChatMessage[],
  options: StreamOptions,
  signal?: AbortSignal,
): Promise<void> {
  const { onChunk, onDone, onError } = options;
  
  const apiKey = getGroqApiKey();
  
  if (!apiKey || apiKey === 'your_groq_api_key_here') {
    onError(new Error('API_KEY_MISSING'));
    return;
  }

  try {
    const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: GROQ_MODEL,
        messages: messages,
        temperature: 0.7,
        max_tokens: 1024,
        stream: true,
      }),
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Groq API Error:', errorData);
      const detail = errorData?.error?.message
        ? ` - ${errorData.error.message}`
        : '';
      throw new Error(`API Error: ${response.status} - ${response.statusText}${detail}`);
    }

    if (!response.body) {
      throw new Error('No response body from API');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          onDone();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || trimmed === 'data: [DONE]') continue;
          if (!trimmed.startsWith('data: ')) continue;

          try {
            const jsonStr = trimmed.slice(6);
            const data = JSON.parse(jsonStr);
            const delta = data.choices?.[0]?.delta?.content;
            
            if (delta) {
              onChunk(delta);
            }
          } catch (parseError) {
            // Skip invalid JSON
            console.warn('Failed to parse chunk:', trimmed);
          }
        }
      }
    } catch (readError) {
      if (signal?.aborted) {
        // Aborted intentionally
        return;
      }
      throw readError;
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    if (signal?.aborted) {
      // Aborted intentionally, don't call onError
      return;
    }
    
    console.error('Stream error:', error);
    
    if (error instanceof Error) {
      if (error.message === 'API_KEY_MISSING') {
        onError(error);
      } else {
        onError(new Error(`Failed to get response: ${error.message}`));
      }
    } else {
      onError(new Error('Unknown error occurred while streaming'));
    }
  }
}

// ============================================
// Non-streaming completion (fallback)
// ============================================
export async function getChatCompletion(
  messages: ChatMessage[],
  signal?: AbortSignal,
): Promise<string> {
  const apiKey = getGroqApiKey();
  
  if (!apiKey || apiKey === 'your_groq_api_key_here') {
    throw new Error('API_KEY_MISSING');
  }

  const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: GROQ_MODEL,
      messages: messages,
      temperature: 0.7,
      max_tokens: 1024,
      stream: false,
    }),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`API Error: ${response.status} - ${errorData.error?.message || response.statusText}`);
  }

  const data = await response.json();
  const content = data.choices?.[0]?.message?.content;
  
  if (!content) {
    throw new Error('No content in response');
  }

  return content;
}

// ============================================
// Get available models
// ============================================
export function getAvailableModels(): string[] {
  return [
    'llama-3.1-8b-instant',
    'llama-3.3-70b-versatile',
  ];
}

// ============================================
// Validate API key (optional)
// ============================================
export async function validateApiKey(apiKey?: string): Promise<boolean> {
  const key = apiKey || getGroqApiKey();
  
  if (!key || key === 'your_groq_api_key_here') {
    return false;
  }

  try {
    const response = await fetch('https://api.groq.com/openai/v1/models', {
      headers: {
        'Authorization': `Bearer ${key}`,
      },
    });
    
    return response.ok;
  } catch (error) {
    return false;
  }
}

